import React, { useCallback, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useDispatch } from 'react-redux';

import AddEditIncumplimiento from './Add_Edit/AddEditIncumplimiento';
import DeleteIncumplimiento from './Delete/DeleteIncumplimiento';
import useIncumplimientosManagement from './Management/useIncumplimientosManagement';
import FiscalizacionSummaryCard from './Management/FiscalizacionSummaryCard';
import IncumplimientosTable from './Management/IncumplimientosTable';
import AddEditUserModal from '../../Admin/Users/Add_Edit/AddEditUserModal';
import { getUserList } from '../../../slices/user/thunk';

const toStringId = value => (value == null ? '' : value.toString());

const parseUserNameFromQuery = query => {
	const cleaned = (query || '').trim().replace(/\s+/g, ' ');
	if (!cleaned) {
		return { first_name: '', last_name: '' };
	}
	const [firstName, ...rest] = cleaned.split(' ');
	return {
		first_name: firstName || '',
		last_name: rest.join(' ').trim(),
	};
};

const IncumplimientosManagement = ({
	dataFiscalizacion,
	isOpen,
	handleToggleModal,
	onDataChange,
}) => {
	const dispatch = useDispatch();
	const [modalAddUser, setModalAddUser] = useState(false);
	const [userActiveTab, setUserActiveTab] = useState('1');
	const [pendingResponsibleQuery, setPendingResponsibleQuery] = useState('');
	const [incumplimientoFormSnapshot, setIncumplimientoFormSnapshot] = useState(null);
	const [switchingToUserModal, setSwitchingToUserModal] = useState(false);

	const {
		dataIncumplimiento,
		modalAdd,
		modalDelete,
		isEdit,
		isEnRevision,
		incumplimientos,
		isLoadingList,
		inspectionTypeOptions,
		inspectionAuthorityOptions,
		responsibleOptions,
		breachResponsibleAreaOptions,
		breachImpactOptions,
		breachOriginOptions,
		breachTypeOfEvidenceOptions,
		breachCategoryOptions,
		breachRiskClassificationOptions,
		handleClickNew,
		handleClickEdit,
		handleClickDelete,
		handleSaveIncumplimiento,
		handleDeleteSuccess,
		setModalAdd,
		setModalDelete,
	} = useIncumplimientosManagement({ dataFiscalizacion, isOpen, onDataChange });

	const handleToggleUserTab = useCallback(tab => {
		if (userActiveTab !== tab) {
			setUserActiveTab(tab);
		}
	}, [userActiveTab]);

	const handleCloseIncumplimientoModal = useCallback(() => {
		setModalAdd(false);
		if (!switchingToUserModal) {
			setIncumplimientoFormSnapshot(null);
		}
		setSwitchingToUserModal(false);
	}, [setModalAdd, switchingToUserModal]);

	const handleRequestCreateResponsible = useCallback((query, formValues) => {
		setPendingResponsibleQuery(query);
		setIncumplimientoFormSnapshot(formValues || null);
		setSwitchingToUserModal(true);
		setModalAdd(false);
		setUserActiveTab('1');
		setModalAddUser(true);
	}, [setModalAdd]);

	const restoreIncumplimientoModal = useCallback(() => {
		setModalAddUser(false);
		setSwitchingToUserModal(false);
		setUserActiveTab('1');
		setModalAdd(true);
	}, [setModalAdd]);

	const handleCloseUserModal = useCallback(() => {
		restoreIncumplimientoModal();
	}, [restoreIncumplimientoModal]);

	const handleUserCreated = useCallback(async createdUser => {
		await dispatch(getUserList());
		const newResponsibleId = toStringId(createdUser?._id);
		if (newResponsibleId) {
			setIncumplimientoFormSnapshot(prev => {
				const previousValues = prev || {};
				const previousResponsible = Array.isArray(previousValues.responsible_user_ids)
					? previousValues.responsible_user_ids
					: [];
				const mergedResponsible = Array.from(
					new Set([...previousResponsible.map(toStringId), newResponsibleId])
				);
				return {
					...previousValues,
					responsible_user_ids: mergedResponsible,
				};
			});
		}
		restoreIncumplimientoModal();
	}, [dispatch, restoreIncumplimientoModal]);

	const handleClickNewManaged = useCallback(() => {
		setIncumplimientoFormSnapshot(null);
		setPendingResponsibleQuery('');
		setSwitchingToUserModal(false);
		handleClickNew();
	}, [handleClickNew]);

	const handleClickEditManaged = useCallback(incumplimiento => {
		setIncumplimientoFormSnapshot(null);
		setPendingResponsibleQuery('');
		setSwitchingToUserModal(false);
		handleClickEdit(incumplimiento);
	}, [handleClickEdit]);

	const initialUserData = parseUserNameFromQuery(pendingResponsibleQuery);

	return (
		<>
			<Modal
				isOpen={isOpen}
				toggle={handleToggleModal}
				backdrop='static'
				centered={true}
				size='xl'
				className='border-0'
				modalClassName='modal fade zoomIn'
			>
				<ModalHeader toggle={handleToggleModal}>
					Incumplimientos -{' '}
					{dataFiscalizacion?.code || dataFiscalizacion?.codigo || 'Fiscalizacion'}
				</ModalHeader>

				<ModalBody className='modal-body'>
					<FiscalizacionSummaryCard dataFiscalizacion={dataFiscalizacion} />

					<div className='my-3'>
						<h5 className='mb-3'>
							Lista de Incumplimientos ({incumplimientos.length})
						</h5>
						<IncumplimientosTable
							incumplimientos={incumplimientos}
							isLoadingList={isLoadingList}
							onEdit={handleClickEditManaged}
							onDelete={handleClickDelete}
						/>
					</div>

					<div className='text-center mt-4'>
						<Button color='primary' className='btn-label' onClick={handleClickNewManaged}>
							<i className='ri-add-line label-icon'></i>
							Agregar Incumplimiento
						</Button>
					</div>
				</ModalBody>

				<ModalFooter>
					<div className='hstack gap-2 justify-content-end'>
						<Button
							type='button'
							onClick={handleToggleModal}
							className='btn-danger btn-label'
						>
							<i className='ri-close-line label-icon'></i>
							Cerrar
						</Button>
					</div>
				</ModalFooter>
			</Modal>

			<AddEditIncumplimiento
				dataIncumplimiento={dataIncumplimiento}
				dataFiscalizacion={dataFiscalizacion}
				isOpen={modalAdd}
				handleToggleModal={handleCloseIncumplimientoModal}
				initialFormValues={incumplimientoFormSnapshot}
				onFormValuesChange={setIncumplimientoFormSnapshot}
				onRequestCreateResponsible={handleRequestCreateResponsible}
				handleSave={handleSaveIncumplimiento}
				isEdit={isEdit}
				tipoFiscalizacion={dataFiscalizacion?.tipo}
				isEnRevision={isEnRevision}
				inspectionTypeOptions={inspectionTypeOptions}
				inspectionAuthorityOptions={inspectionAuthorityOptions}
				responsibleOptions={responsibleOptions}
				breachResponsibleAreaOptions={breachResponsibleAreaOptions}
				breachImpactOptions={breachImpactOptions}
				breachOriginOptions={breachOriginOptions}
				breachTypeOfEvidenceOptions={breachTypeOfEvidenceOptions}
				breachCategoryOptions={breachCategoryOptions}
				breachRiskClassificationOptions={breachRiskClassificationOptions}
			/>

			<AddEditUserModal
				dataUser={{}}
				initialUserData={initialUserData}
				isOpen={modalAddUser}
				isEdit={false}
				handleToggleModal={handleCloseUserModal}
				onRequestClose={handleCloseUserModal}
				onUserCreated={handleUserCreated}
				activeTab={userActiveTab}
				toggleTab={handleToggleUserTab}
			/>

			<DeleteIncumplimiento
				dataIncumplimiento={dataIncumplimiento}
				isOpen={modalDelete}
				handleToggleModal={() => setModalDelete(false)}
				onDeleteSuccess={handleDeleteSuccess}
			/>
		</>
	);
};

export default IncumplimientosManagement;